/**
 * ABI encoders for all Ostium trading contract function calls.
 */

import { type Address, type Hex, encodeFunctionData, getAddress } from 'viem';
import { TRADING_ABI } from './internal/contract.js';
import { ERC20_ABI } from './internal/erc20.js';
import type { OstiumTrade, BuilderFee } from './internal/ostium-schema.js';
import { OpenOrderType } from './internal/ostium-schema.js';
import type { EncodedTx } from './types.js';

function tx(to: Address, data: Hex): EncodedTx {
  return { to, data, value: 0n };
}

// ────────────────────────────────────────────────────────────────────────────
// Trading contract encoders
// ────────────────────────────────────────────────────────────────────────────

export function encodeOpenTrade(
  trade: OstiumTrade,
  builderFee: BuilderFee,
  orderType: OpenOrderType,
  slippageP: bigint,
  tradingAddress: Address,
): EncodedTx {
  const data = encodeFunctionData({
    abi: TRADING_ABI,
    functionName: 'openTrade',
    args: [
      {
        collateral: trade.collateral,
        openPrice: trade.openPrice,
        tp: trade.tp,
        sl: trade.sl,
        trader: trade.trader,
        leverage: Number(trade.leverage),
        pairIndex: Number(trade.pairIndex),
        index: Number(trade.index),
        buy: trade.buy,
        isDayTrade: trade.isDayTrade ?? false
      },
      {
        builder: builderFee.builder,
        builderFee: Number(builderFee.builderFee),
      },
      orderType,
      slippageP,
    ],
  });
  return tx(tradingAddress, data);
}

export function encodeCloseTradeMarket(
  pairIndex: bigint,
  index: bigint,
  closePercentage: bigint,
  marketPrice: bigint,
  slippageP: bigint,
  tradingAddress: Address,
): EncodedTx {
  const data = encodeFunctionData({
    abi: TRADING_ABI,
    functionName: 'closeTradeMarket',
    args: [Number(pairIndex), Number(index), Number(closePercentage), marketPrice, Number(slippageP)],
  });
  return tx(tradingAddress, data);
}

export function encodeCancelLimitOrder(
  pairIndex: bigint,
  orderIndex: bigint,
  tradingAddress: Address,
): EncodedTx {
  const data = encodeFunctionData({
    abi: TRADING_ABI,
    functionName: 'cancelOpenLimitOrder',
    args: [Number(pairIndex), Number(orderIndex)],
  });
  return tx(tradingAddress, data);
}

export function encodeCloseTradeMarketTimeout(
  orderId: bigint,
  retry: boolean,
  tradingAddress: Address,
): EncodedTx {
  const data = encodeFunctionData({
    abi: TRADING_ABI,
    functionName: 'closeTradeMarketTimeout',
    args: [orderId, retry],
  });
  return tx(tradingAddress, data);
}

export function encodeOpenTradeMarketTimeout(
  orderId: bigint,
  tradingAddress: Address,
): EncodedTx {
  const data = encodeFunctionData({
    abi: TRADING_ABI,
    functionName: 'openTradeMarketTimeout',
    args: [orderId],
  });
  return tx(tradingAddress, data);
}

export function encodeUpdateOpenLimitOrder(
  pairIndex: bigint,
  orderIndex: bigint,
  price: bigint,
  tp: bigint,
  sl: bigint,
  tradingAddress: Address,
): EncodedTx {
  const data = encodeFunctionData({
    abi: TRADING_ABI,
    functionName: 'updateOpenLimitOrder',
    args: [Number(pairIndex), Number(orderIndex), price, tp, sl],
  });
  return tx(tradingAddress, data);
}

export function encodeUpdateTp(
  pairIndex: bigint,
  index: bigint,
  newTp: bigint,
  tradingAddress: Address,
): EncodedTx {
  const data = encodeFunctionData({
    abi: TRADING_ABI,
    functionName: 'updateTp',
    args: [Number(pairIndex), Number(index), newTp],
  });
  return tx(tradingAddress, data);
}

export function encodeUpdateSl(
  pairIndex: bigint,
  index: bigint,
  newSl: bigint,
  tradingAddress: Address,
): EncodedTx {
  const data = encodeFunctionData({
    abi: TRADING_ABI,
    functionName: 'updateSl',
    args: [Number(pairIndex), Number(index), newSl],
  });
  return tx(tradingAddress, data);
}

export function encodeTopUpCollateral(
  pairIndex: bigint,
  index: bigint,
  amount: bigint,
  tradingAddress: Address,
): EncodedTx {
  const data = encodeFunctionData({
    abi: TRADING_ABI,
    functionName: 'topUpCollateral',
    args: [Number(pairIndex), Number(index), amount],
  });
  return tx(tradingAddress, data);
}

export function encodeRemoveCollateral(
  pairIndex: bigint,
  index: bigint,
  amount: bigint,
  tradingAddress: Address,
): EncodedTx {
  const data = encodeFunctionData({
    abi: TRADING_ABI,
    functionName: 'removeCollateral',
    args: [Number(pairIndex), Number(index), amount],
  });
  return tx(tradingAddress, data);
}

export function encodeSetDelegate(
  delegateAddress: Address,
  tradingAddress: Address,
): EncodedTx {
  const data = encodeFunctionData({
    abi: TRADING_ABI,
    functionName: 'setDelegate',
    args: [getAddress(delegateAddress)],
  });
  return tx(tradingAddress, data);
}

export function encodeRemoveDelegate(tradingAddress: Address): EncodedTx {
  const data = encodeFunctionData({
    abi: TRADING_ABI,
    functionName: 'removeDelegate',
    args: [],
  });
  return tx(tradingAddress, data);
}

/**
 * Wraps inner calldata in delegatedAction(trader, callData).
 * Used internally by the DelegatedSignerStrategy.
 */
export function encodeDelegatedAction(
  traderAddress: Address,
  callData: Hex,
  tradingAddress: Address,
): EncodedTx {
  const data = encodeFunctionData({
    abi: TRADING_ABI,
    functionName: 'delegatedAction',
    args: [getAddress(traderAddress), callData],
  });
  return tx(tradingAddress, data);
}

// ────────────────────────────────────────────────────────────────────────────
// ERC-20 encoders
// ────────────────────────────────────────────────────────────────────────────

export function encodeUsdcApprove(
  spender: Address,
  amount: bigint,
  usdcAddress: Address,
): EncodedTx {
  const data = encodeFunctionData({
    abi: ERC20_ABI,
    functionName: 'approve',
    args: [getAddress(spender), amount],
  });
  return tx(usdcAddress, data);
}

export function encodeErc20Transfer(
  to: Address,
  amount: bigint,
  tokenAddress: Address,
): EncodedTx {
  const data = encodeFunctionData({
    abi: ERC20_ABI,
    functionName: 'transfer',
    args: [getAddress(to), amount],
  });
  return tx(tokenAddress, data);
}
